import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../lib/useApi.js';
import { PageHeader } from '../../app/PageHeader.jsx';
import { useClientPagination } from '../../lib/usePagination.js';
import {
  ErrorState,
  StatusChip,
  StagePipeline,
  AlertCard,
  PageViewerControls,
  KpiHeroBand,
  KpiCard,
  WorkflowPipeline,
  buildPipelineSteps,
  InlineScoreCell,
  KpiBandSkeleton,
  TableSkeleton,
  BrandedEmptyState,
  StaggerReveal,
} from '../../components/index.js';
import './lifecycle.css';
import './lifecycle.redesign.css';

const PIPELINE_STAGES = [
  { id: 'intake', label: 'Intake', matchers: ['intake', 'concept'] },
  {
    id: 'placemat',
    label: 'Placemat',
    matchers: ['placemat', 'business case', 'business_case', 'businesscase'],
  },
  { id: 'scoping', label: 'Scoping', matchers: ['scoping', 'approval', 'scope'] },
  { id: 'diagnose', label: 'Diagnose', matchers: ['diagnose', 'discovery'] },
  {
    id: 'implement',
    label: 'Implement',
    matchers: ['implement', 'implementation', 'execution', 'development', 'integration'],
  },
  {
    id: 'sustain_transition',
    label: 'Sustain/Transition',
    matchers: ['sustainment', 'sustain', 'transition', 'production', 'operate'],
  },
  { id: 'closeout', label: 'Closeout', matchers: ['closeout', 'closure'] },
  { id: 'lessons_learned', label: 'Lessons Learned', matchers: ['lesson'] },
  {
    id: 'archived_completed',
    label: 'Archived/Completed',
    matchers: ['archived', 'archive', 'completed', 'complete', 'closed', 'retirement'],
  },
];

function stageFor(p) {
  const s = String(p.lifecycleStage || p.status || '').toLowerCase();
  for (const st of PIPELINE_STAGES) {
    if (st.matchers.some((m) => s.includes(m))) return st.id;
  }
  return 'execution';
}

function stageLabelFor(p) {
  const id = p.lifecycleStage || stageFor(p);
  const match = PIPELINE_STAGES.find(
    (st) => st.id === id || st.matchers.some((m) => String(id).toLowerCase().includes(m)),
  );
  return match ? match.label : String(id).replace(/_/g, ' ');
}

// Tone for a project's card accent, derived from the live stage status the API
// computed (done -> sage, active -> amber, blocked -> brick). Falls back to a
// neutral "blue" for the synthetic 'execution' bucket that has no stage record.
function cardToneForProject(p, stageById) {
  const id = stageFor(p);
  const stage = stageById.get(id);
  if (!stage) return 'blue';
  if (stage.status === 'blocked') return 'brick';
  if (stage.status === 'active') return 'amber';
  if (stage.status === 'done') return 'sage';
  return 'slate';
}

export function PortfolioLifecycleManagementPage() {
  const navigate = useNavigate();
  const [selectedStage, setSelectedStage] = useState(null);
  const projects = useApi('/projects', { query: { limit: 200 } });
  const summary = useApi('/lifecycle/summary');

  const projectList = useMemo(() => projects.data?.data || [], [projects.data]);
  const lifecycleSummary = summary.data?.data || null;

  const stages = useMemo(() => {
    return (lifecycleSummary?.stages || []).map((stage) => ({
      ...stage,
      value: stage.valueLabel,
      ribbon:
        stage.pendingEvidenceCount > 4
          ? { label: 'Evidence backlog', tone: 'amber' }
          : stage.count >= 5
            ? { label: 'High volume', tone: 'sage' }
            : null,
    }));
  }, [lifecycleSummary]);

  const stageById = useMemo(() => new Map(stages.map((s) => [s.id, s])), [stages]);

  const filtered = useMemo(() => {
    if (!selectedStage) return projectList;
    return projectList.filter((p) => stageFor(p) === selectedStage);
  }, [projectList, selectedStage]);
  const projectPager = useClientPagination(filtered, {
    initialPageSize: 8,
    resetKey: selectedStage || 'all',
  });

  const dwellMetric = lifecycleSummary?.metricSeries?.dwellDays;
  const passMetric = lifecycleSummary?.metricSeries?.gatePassRate;
  const dwellCurrent = useMemo(() => {
    if (selectedStage) {
      return stages.find((stage) => stage.id === selectedStage)?.avgDwellDays || 0;
    }
    if (!stages.length) return 0;
    return Math.round(
      stages.reduce((sum, stage) => sum + Number(stage.avgDwellDays || 0), 0) / stages.length,
    );
  }, [selectedStage, stages]);
  const passCurrent = useMemo(() => {
    if (selectedStage) {
      return stages.find((stage) => stage.id === selectedStage)?.passRatePct || 0;
    }
    if (!stages.length) return 0;
    return Math.round(
      stages.reduce((sum, stage) => sum + Number(stage.passRatePct || 0), 0) / stages.length,
    );
  }, [selectedStage, stages]);

  // Benchmark the current dwell against the API-provided per-stage benchmark so
  // the dwell KpiCard tone is data-driven (lower is better -> invert).
  const dwellBenchmark = useMemo(() => {
    if (selectedStage) {
      return stages.find((stage) => stage.id === selectedStage)?.benchmarkDays || 0;
    }
    if (!stages.length) return 0;
    return Math.round(
      stages.reduce((sum, stage) => sum + Number(stage.benchmarkDays || 0), 0) / stages.length,
    );
  }, [selectedStage, stages]);

  // Portfolio-wide evidence burden, summed straight from the live per-stage
  // pendingEvidenceCount values the API derives from gate criteria.
  const totalPendingEvidence = useMemo(
    () => stages.reduce((sum, stage) => sum + Number(stage.pendingEvidenceCount || 0), 0),
    [stages],
  );

  // The single most-pressured stage drives the derived alert below. We rank by
  // tone severity (blocked > active) then by raw pending-evidence count so the
  // alert always names a real stage with a real, current backlog number.
  const alertStage = useMemo(() => {
    if (!stages.length) return null;
    const ranked = [...stages].sort((a, b) => {
      const sev = (s) => (s.status === 'blocked' ? 2 : s.status === 'active' ? 1 : 0);
      if (sev(b) !== sev(a)) return sev(b) - sev(a);
      return Number(b.pendingEvidenceCount || 0) - Number(a.pendingEvidenceCount || 0);
    });
    const top = ranked[0];
    // Only surface a real concern: a blocked stage, or a measurable backlog /
    // over-benchmark dwell. Otherwise the portfolio reads clear.
    const overBenchmark =
      Number(top.avgDwellDays || 0) > Number(top.benchmarkDays || 0) &&
      Number(top.benchmarkDays || 0) > 0;
    if (top.status === 'blocked' || top.pendingEvidenceCount > 0 || overBenchmark) {
      return top;
    }
    return null;
  }, [stages]);

  // Stage progression ribbon — read-only "where the portfolio sits" view built
  // from the same live stage records (the API already classifies each stage as
  // done / active / blocked).
  const flowSteps = useMemo(
    () =>
      buildPipelineSteps({
        order: stages.map((s) => s.id),
        current: null,
        statusByKey: Object.fromEntries(
          stages.map((s) => [
            s.id,
            s.status === 'blocked' ? 'blocked' : s.status === 'active' ? 'active' : 'done',
          ]),
        ),
        labels: Object.fromEntries(stages.map((s) => [s.id, s.label])),
      }),
    [stages],
  );

  const heroItems = useMemo(() => {
    const dwellOk = dwellBenchmark > 0 ? dwellCurrent <= dwellBenchmark : null;
    return [
      {
        id: 'initiatives',
        label: 'Initiatives in lifecycle',
        value: projectList.length,
        icon: '🛰️',
        status: 'blue',
        statusLabel: selectedStage ? 'Filtered' : 'Portfolio',
        footer: `${stages.length} stage gates`,
      },
      {
        id: 'dwell',
        label: 'Avg time in stage',
        value: dwellCurrent,
        unit: 'days',
        sparkline: dwellMetric?.values || [],
        sparklineTone: 'blue',
        status: dwellOk == null ? 'slate' : dwellOk ? 'sage' : 'amber',
        statusLabel: dwellBenchmark > 0 ? (dwellOk ? 'On benchmark' : 'Over benchmark') : undefined,
        footer: dwellBenchmark > 0 ? `Benchmark ${dwellBenchmark} days` : undefined,
      },
      {
        id: 'pass-rate',
        label: 'Gate pass rate',
        value: passCurrent,
        unit: '%',
        sparkline: passMetric?.values || [],
        sparklineTone: 'sage',
        status: passCurrent >= 85 ? 'sage' : passCurrent >= 70 ? 'amber' : 'brick',
        statusLabel: passCurrent >= 85 ? 'Healthy' : passCurrent >= 70 ? 'Watch' : 'At risk',
      },
      {
        id: 'pending-evidence',
        label: 'Pending evidence',
        value: totalPendingEvidence,
        unit: 'items',
        icon: '📎',
        status: totalPendingEvidence === 0 ? 'sage' : totalPendingEvidence > 8 ? 'brick' : 'amber',
        statusLabel: totalPendingEvidence === 0 ? 'Clear' : 'Open',
        footer: 'Across all stage gates',
      },
    ];
  }, [
    projectList.length,
    stages.length,
    selectedStage,
    dwellCurrent,
    dwellBenchmark,
    passCurrent,
    totalPendingEvidence,
    dwellMetric,
    passMetric,
  ]);

  if (projects.loading || summary.loading) {
    return (
      <div className="lifecycle-redesign">
        <KpiBandSkeleton count={4} />
        <div className="card">
          <TableSkeleton rows={6} />
        </div>
      </div>
    );
  }
  if (projects.error) return <ErrorState error={projects.error} />;
  if (summary.error) return <ErrorState error={summary.error} />;

  return (
    <div className="lifecycle-redesign">
      {/* PRIME-SHELL-UX-001: single body page identity + relocated About. */}
      <PageHeader />
      <KpiHeroBand
        eyebrow="Portfolio lifecycle posture"
        asOf={lifecycleSummary?.asOf || summary.loadedAt}
        items={heroItems}
        columns={4}
      />

      <section className="bento lifecycle-bento">
        <div className="span-12 card lifecycle-pipeline-wrap">
          <header className="row spread">
            <div>
              <div className="eyebrow">Stage-gate pipeline</div>
              <h2 className="card-title">Nine stages · {projectList.length} initiatives</h2>
            </div>
            <span className="muted small">Click a stage to filter</span>
          </header>
          <StagePipeline
            stages={stages}
            variant="pipeline"
            selectedId={selectedStage}
            onSelect={(s) => setSelectedStage((cur) => (cur === s.id ? null : s.id))}
          />
          {flowSteps.length ? (
            <div className="lifecycle-flow">
              <WorkflowPipeline steps={flowSteps} ariaLabel="Lifecycle stage progression" />
            </div>
          ) : null}
        </div>

        <div className="span-8 card lifecycle-matrix">
          <header className="row spread">
            <div>
              <div className="eyebrow">Active lifecycle matrix</div>
              <h2 className="card-title">
                {selectedStage
                  ? `Filtered: ${PIPELINE_STAGES.find((s) => s.id === selectedStage)?.label}`
                  : 'All initiatives'}
              </h2>
            </div>
            {selectedStage ? (
              <button className="btn link" onClick={() => setSelectedStage(null)}>
                Clear filter
              </button>
            ) : null}
          </header>
          {filtered.length === 0 ? (
            <BrandedEmptyState
              icon="🗂️"
              eyebrow="Lifecycle matrix"
              title="No initiatives in this stage"
              description="No initiatives currently map to the selected lifecycle stage. Clear the filter to see the full portfolio."
              tone="slate"
              action={
                selectedStage ? (
                  <button className="btn link" onClick={() => setSelectedStage(null)}>
                    Clear filter →
                  </button>
                ) : null
              }
            />
          ) : (
            <StaggerReveal className="lifecycle-matrix-grid">
              {projectPager.items.map((p) => {
                const tone = cardToneForProject(p, stageById);
                const impact = p.healthScores?.missionImpact;
                return (
                  <article
                    key={p.id}
                    className="lifecycle-card"
                    data-stage-tone={tone}
                    onClick={() => navigate(`/projects/${p.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      (e.key === 'Enter' || e.key === ' ') && navigate(`/projects/${p.id}`)
                    }
                  >
                    <header className="row spread">
                      <strong>{p.name}</strong>
                      <StatusChip status={p.status} />
                    </header>
                    <div className="lifecycle-card__meta muted small">
                      {p.id} · {p.programId || 'program n/a'}
                    </div>
                    <div className="row spread" style={{ marginTop: 6 }}>
                      <span className="muted small">Stage</span>
                      <span className="chip outline sm">{stageLabelFor(p)}</span>
                    </div>
                    <div className="lifecycle-card__impact">
                      <span className="muted small">Mission impact</span>
                      {impact == null ? (
                        <span className="lifecycle-card__impact-empty small">—</span>
                      ) : (
                        <InlineScoreCell
                          value={impact}
                          max={100}
                          ariaLabel={`Mission impact ${impact} of 100`}
                        />
                      )}
                    </div>
                  </article>
                );
              })}
            </StaggerReveal>
          )}
          <PageViewerControls
            page={projectPager.page}
            label="initiatives"
            pageSize={projectPager.pageSize}
            onOffsetChange={projectPager.setOffset}
            onPageSizeChange={projectPager.setPageSize}
          />
        </div>

        <div className="span-4 lifecycle-metric-rail">
          <KpiCard
            label="Avg time in stage"
            value={dwellCurrent}
            unit="days"
            sparkline={dwellMetric?.values || []}
            sparklineTone="blue"
            status={
              dwellBenchmark > 0 ? (dwellCurrent <= dwellBenchmark ? 'sage' : 'amber') : undefined
            }
            statusLabel={
              dwellBenchmark > 0
                ? dwellCurrent <= dwellBenchmark
                  ? 'On benchmark'
                  : 'Over benchmark'
                : undefined
            }
            footer={dwellBenchmark > 0 ? `Benchmark ${dwellBenchmark} days` : undefined}
          />
          <KpiCard
            label="Gate pass rate"
            value={passCurrent}
            unit="%"
            sparkline={passMetric?.values || []}
            sparklineTone="sage"
            status={passCurrent >= 85 ? 'sage' : passCurrent >= 70 ? 'amber' : 'brick'}
            statusLabel={passCurrent >= 85 ? 'Healthy' : passCurrent >= 70 ? 'Watch' : 'At risk'}
          />
          {alertStage ? (
            <AlertCard
              severity={alertStage.status === 'blocked' ? 'red' : 'amber'}
              title={`${alertStage.label} stage needs attention`}
              body={
                alertStage.pendingEvidenceCount > 0
                  ? `${alertStage.count} ${
                      alertStage.count === 1 ? 'initiative' : 'initiatives'
                    } in ${alertStage.label} with ${alertStage.pendingEvidenceCount} pending evidence ${
                      alertStage.pendingEvidenceCount === 1 ? 'item' : 'items'
                    } · ${alertStage.avgDwellDays}d avg dwell vs ${alertStage.benchmarkDays}d benchmark. Schedule a triage with the Portfolio Manager.`
                  : `${alertStage.label} is running ${alertStage.avgDwellDays}d avg dwell against a ${alertStage.benchmarkDays}d benchmark. Schedule a triage with the Portfolio Manager.`
              }
              chips={[
                `${alertStage.passRatePct}% pass rate`,
                `${alertStage.valueLabel} planned value`,
              ]}
              actionLabel="Filter to this stage"
              onAction={() => setSelectedStage(alertStage.id)}
            />
          ) : (
            <AlertCard
              severity="green"
              title="Lifecycle pipeline is clear"
              body="No stage currently carries a pending-evidence backlog or is running over its dwell benchmark. The portfolio is inside its expected operating band."
            />
          )}
        </div>
      </section>
    </div>
  );
}
